import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function CozyCoffeeShopsRainyDays() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "cozy-coffee-shops-rainy-days";

  // 2) Access articles from your context (if applicable)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Greenhorn Espresso Bar (West End)",
    "section-2": "Lost + Found Café (Gastown/Downtown Eastside)",
    "section-3": "Dalina Café (Chinatown)",
    "section-4": "The Garden Strathcona (Strathcona)",
    "section-5": "Revolver Coffee (Gastown)",
    "section-6": "Platform 7 Coffee (East Vancouver)",
    "section-7": "Le Marché St. George (Riley Park/Kensington)",
    "section-8": "Beaucoup Bakery & Café (Kitsilano)",
    "section-9": "Secret Garden Tea Company (Kerrisdale)",
    "local-best-practices": "Local Best Practices & Considerations",
    faq: "FAQ",
    "local-regulations": "Local Regulations / Best Practices",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver",
    },
    {
      url: "https://secretgardentea.com/",
      label: "Secret Garden Tea Company – Official Site",
    },
    {
      url: "https://beaucoupbakery.com/",
      label: "Beaucoup Bakery – Official Website",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    // section-1: Greenhorn Espresso Bar
    {
      id: "section-1",
      lat: 49.2830,
      lng: -123.1360,
      popupText: "Greenhorn Espresso Bar (West End)",
    },
    // section-2: Lost + Found Café
    {
      id: "section-2",
      lat: 49.2817,
      lng: -123.1060,
      popupText: "Lost + Found Café (Gastown/Downtown Eastside)",
    },
    // section-3: Dalina Café
    {
      id: "section-3",
      lat: 49.2790,
      lng: -123.1000,
      popupText: "Dalina Café (Chinatown)",
    },
    // section-4: The Garden Strathcona
    {
      id: "section-4",
      lat: 49.2810,
      lng: -123.0860,
      popupText: "The Garden Strathcona (Strathcona)",
    },
    // section-5: Revolver Coffee
    {
      id: "section-5",
      lat: 49.2822,
      lng: -123.1085,
      popupText: "Revolver Coffee (Gastown)",
    },
    // section-6: Platform 7 Coffee
    {
      id: "section-6",
      lat: 49.2810,
      lng: -123.0590,
      popupText: "Platform 7 Coffee (East Vancouver)",
    },
    // section-7: Le Marché St. George
    {
      id: "section-7",
      lat: 49.2440,
      lng: -123.0968,
      popupText: "Le Marché St. George (Riley Park/Kensington)",
    },
    // section-8: Beaucoup Bakery & Café
    {
      id: "section-8",
      lat: 49.2663,
      lng: -123.1403,
      popupText: "Beaucoup Bakery & Café (Kitsilano)",
    },
    // section-9: Secret Garden Tea Company
    {
      id: "section-9",
      lat: 49.2341,
      lng: -123.1553,
      popupText: "Secret Garden Tea Company (Kerrisdale)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>5 Cozy Coffee Shops in Vancouver for Rainy Days</title>
        <meta
          name="description"
          content="Explore some of the coziest coffee shops in Vancouver where you can stay warm and dry on rainy days."
        />
        <meta
          name="keywords"
          content="coffee shops, rainy days, Vancouver, cozy cafes"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/cozy-coffee-shops-rainy-days.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="5 Cozy Coffee Shops in Vancouver for Rainy Days"
        />
        <meta
          property="og:description"
          content="Looking for a warm and welcoming spot in Vancouver during the rain? Here are five must-visit cozy coffee shops."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/cozy-coffee-shops-rainy-days.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/cozy-coffee-shops-rainy-days.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="5 Cozy Coffee Shops in Vancouver for Rainy Days"
        />
        <meta
          name="twitter:description"
          content="Escape the drizzle in these warm, welcoming Vancouver coffee shops."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/cozy-coffee-shops-rainy-days.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/cozy-coffee-shops-rainy-days.jpg"
              }
              alt="Cozy coffee shop interior in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is famous for its rainy weather, but that only adds to
              the charm of the city’s coffee culture. When the skies are grey
              and the drizzle is steady, there’s nothing more comforting than
              stepping into a warm, bustling cafe. This guide highlights some of
              Vancouver’s coziest coffee shops—places where you can enjoy a
              perfectly brewed coffee, indulge in a fresh pastry, and watch the
              rain fall from a snug seat indoors.
            </p>

            {/* SECTION 1: Greenhorn Espresso Bar */}
            <h2 id="section-1">Greenhorn Espresso Bar (West End)</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2830}
                coordX={-123.1360}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              994 Nicola Street, Vancouver, BC V6G 2C8
            </p>
            <p>
              Greenhorn Espresso Bar is a West End gem, offering rustic decor and
              warm lighting that creates a perfectly cozy ambiance. Its two-level
              space features an upstairs loft filled with vintage vinyl records.
              On a rainy day, sink into a comfortable seat while jazz music
              softly plays in the background. 
            </p>
            <ul>
              <li><strong>Hours:</strong> Mon–Fri 7 AM – 5 PM; Sat–Sun 8 AM – 5 PM</li>
              <li><strong>Cost:</strong> ~ $10 for a coffee + pastry</li>
              <li>
                <strong>Highlights:</strong> Brunch items, in-house baked goods,
                and free Wi-Fi (turned off at peak lunch hour for a digital
                break).
              </li>
            </ul>

            {/* SECTION 2: Lost + Found Café */}
            <h2 id="section-2">Lost + Found Café (Gastown/Downtown Eastside)</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2817}
                coordX={-123.1060}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              33 West Hastings Street, Vancouver, BC V6B 1G4
            </p>
            <p>
              Lost + Found Café is a spacious, bohemian refuge on the edge of
              Gastown. The artsy interior offers comfy couches and plenty of
              corners to claim as your own. Its large windows let you watch the
              rain drip down while staying warm indoors. The cafe often showcases
              local art, making it a creative haven for artists and visitors
              alike.
            </p>
            <ul>
              <li><strong>Hours:</strong> Mon–Fri 9 AM – 3:30 PM; Closed Weekends</li>
              <li><strong>Cost:</strong> ~ $8–$12 for coffee + snack</li>
              <li>
                <strong>Highlights:</strong> Community vibes, in-house gallery
                space, free Wi-Fi.
              </li>
            </ul>

            {/* SECTION 3: Dalina Café */}
            <h2 id="section-3">Dalina Café (Chinatown)</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2790}
                coordX={-123.1000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              687 Main Street, Vancouver, BC V6A 2V4
            </p>
            <p>
              Dalina blends a clean, modern café atmosphere with an Italian-style
              marketplace. Warm wooden accents, a heated covered patio, and
              bright interiors make this a cozy oasis in the heart of Chinatown.
              Enjoy an Americano paired with a fresh pastry or a house-made
              panini. Even in the rain, the covered patio lets you savor the
              outdoors while staying comfortably dry.
            </p>
            <ul>
              <li><strong>Hours:</strong> Mon–Fri 7 AM – 5 PM; Sat–Sun 8 AM – 5 PM</li>
              <li><strong>Cost:</strong> ~ $9–$12 for coffee + snack</li>
              <li>
                <strong>Highlights:</strong> Heated patio, free Wi-Fi,
                vegan/gluten-free treats, small market area for gourmet groceries.
              </li>
            </ul>

            {/* SECTION 4: The Garden Strathcona */}
            <h2 id="section-4">The Garden Strathcona (Strathcona)</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2810}
                coordX={-123.0860}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              868 East Hastings Street, Vancouver, BC V6A 1R6
            </p>
            <p>
              The Garden Strathcona lives up to its name with lush greenery
              adorning the interior. Large windows and abundant potted plants
              create a bright, tranquil environment—perfect for forgetting the
              grey skies outside. Savor a latte or a bowl of soup made with local
              ingredients. With a warm vibe and ample seating, this is an ideal
              refuge from the drizzle.
            </p>
            <ul>
              <li><strong>Hours:</strong> Daily 9 AM – 4 PM</li>
              <li><strong>Cost:</strong> ~ $10–$15 for coffee + meal</li>
              <li>
                <strong>Highlights:</strong> Plant-filled interior, free Wi-Fi,
                in-house bakery, events space upstairs.
              </li>
            </ul>

            {/* SECTION 5: Revolver Coffee */}
            <h2 id="section-5">Revolver Coffee (Gastown)</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2822}
                coordX={-123.1085}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              325 Cambie Street, Vancouver, BC V6B 2N4
            </p>
            <p>
              For serious coffee aficionados, Revolver is a must. Known for
              rotating beans and meticulous brewing methods, it’s often called a
              coffee-snob haven. The wood-accented interior, complete with
              vintage bulbs, adds a cozy flair. Seating is limited, but if you
              can claim a spot, it’s a treat to sip a hand-brewed pour-over while
              rain drips on Gastown’s cobblestone streets.
            </p>
            <ul>
              <li><strong>Hours:</strong> Mon–Sat 7:30 AM – 5 PM; Closed Sundays</li>
              <li><strong>Cost:</strong> ~ $5 pour-over; $8–$10 for coffee + pastry</li>
              <li>
                <strong>Highlights:</strong> No Wi-Fi (focus on enjoying the
                coffee), variety of brewing methods, communal table.
              </li>
            </ul>

            {/* SECTION 6: Platform 7 Coffee */}
            <h2 id="section-6">Platform 7 Coffee (East Vancouver)</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2810}
                coordX={-123.0590}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              2331 East Hastings Street, Vancouver, BC V5L 1V6
            </p>
            <p>
              Platform 7 transports visitors to a vintage European train station,
              complete with ornate ironwork and exposed brick. The whimsical
              decor, combined with relaxed music, provides a unique and cozy
              environment—perfect for reading or catching up with friends. The
              space is roomy for a Vancouver café, and free Wi-Fi makes it
              laptop-friendly.
            </p>
            <ul>
              <li><strong>Hours:</strong> Daily 8 AM – 5 PM</li>
              <li><strong>Cost:</strong> ~ $8 for coffee + treat</li>
              <li>
                <strong>Highlights:</strong> Quirky train-station theme, multiple
                brew methods, locally sourced baked goods.
              </li>
            </ul>

            {/* SECTION 7: Le Marché St. George */}
            <h2 id="section-7">Le Marché St. George (Riley Park/Kensington)</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2440}
                coordX={-123.0968}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              4393 St. George Street, Vancouver, BC V5V 4A3
            </p>
            <p>
              Le Marché St. George is a neighborhood favorite, housed in a
              century-old building with vintage touches. The intimate interior
              has mismatched chairs and a general store vibe—perfect for whiling
              away a rainy afternoon with a latte and a crêpe. Seating is limited
              (and often communal), but that only adds to the cozy, homey feel.
            </p>
            <ul>
              <li><strong>Hours:</strong> Tue–Sun 8 AM – 5 PM; Closed Mondays</li>
              <li><strong>Cost:</strong> ~ $10–$15 for coffee + light meal</li>
              <li>
                <strong>Highlights:</strong> Rustic decor, communal seating, local
                artisanal products for sale, authentic French-style crêpes.
              </li>
            </ul>

            {/* SECTION 8: Beaucoup Bakery & Café */}
            <h2 id="section-8">Beaucoup Bakery & Café (Kitsilano)</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2663}
                coordX={-123.1403}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              2150 Fir Street, Vancouver, BC V6J 3B5
            </p>
            <p>
              This chic French-inspired bakery is known for decadent pastries and
              exceptional espresso drinks. The scent of fresh butter and coffee
              greets you at the door, making it a perfect refuge from damp
              weather. Seating is limited, but if you manage to find a spot,
              you’ll feel wrapped in the warm glow of its stylish interior.
            </p>
            <ul>
              <li><strong>Hours:</strong> Daily 8 AM – 5 PM</li>
              <li><strong>Cost:</strong> ~$8–$11 for coffee + pastry</li>
              <li>
                <strong>Highlights:</strong> Award-winning croissants, boutique
                atmosphere, open kitchen concept.
              </li>
            </ul>

            {/* SECTION 9: Secret Garden Tea Company */}
            <h2 id="section-9">Secret Garden Tea Company (Kerrisdale)</h2>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2341}
                coordX={-123.1553}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              2138 West 40th Avenue, Vancouver, BC V6M 1W5
            </p>
            <p>
              Though not strictly a coffee shop, Secret Garden Tea Company is too
              cozy to ignore. This whimsical tea house offers a classic afternoon
              tea service that’s perfect for rainy-day indulgence. Plush seating,
              floral accents, and mismatched vintage china make for a truly
              inviting ambience. 
            </p>
            <ul>
              <li><strong>Hours:</strong> Mon–Fri 10 AM – 4 PM; Sat–Sun 10 AM – 6 PM</li>
              <li><strong>Cost:</strong> ~$40 for High Tea service; ~$15 for tea + scone</li>
              <li>
                <strong>Highlights:</strong> Extensive loose-leaf tea menu,
                reservations recommended for High Tea, cozy décor with floral
                accents.
              </li>
            </ul>

            {/* SECTION: Local Best Practices & Considerations */}
            <h2 id="local-best-practices">Local Best Practices & Considerations</h2>
            <p>
              Vancouver’s coffee shop culture values comfort, sustainability, and
              community. Here are some tips:
            </p>
            <ul>
              <li>
                <strong>Etiquette:</strong> Purchase at least one item before
                settling in with your laptop; if you plan to linger, consider
                buying a second drink or snack.
              </li>
              <li>
                <strong>Eco-consciousness:</strong> Bringing your own reusable
                mug is encouraged; some cafes offer a small discount for that.
              </li>
              <li>
                <strong>Peak Hours:</strong> Mornings and weekends can be busy;
                if you need a quiet space, aim for mid-afternoons or weekdays.
              </li>
              <li>
                <strong>Wi-Fi Policies:</strong> Some cafes unplug Wi-Fi during
                peak hours to encourage conversation (e.g., Greenhorn at lunch).
              </li>
            </ul>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Which cafe is best for working or studying?</h3>
              <p>
                Lost + Found Café and Greenhorn Espresso Bar are spacious and
                welcoming for laptop users. The Garden Strathcona also has a calm
                ambiance and free Wi-Fi. However, Revolver and Le Marché St.
                George are less laptop-friendly due to smaller seating and/or
                busy atmospheres.
              </p>

              <h3>2. Do these cafes offer gluten-free or vegan options?</h3>
              <p>
                Yes. Dalina offers vegan and gluten-free treats, The Garden
                Strathcona also has a variety of baked goods catering to dietary
                restrictions. Many Vancouver cafes accommodate special diets.
              </p>

              <h3>3. What is the average coffee price in Vancouver?</h3>
              <p>
                A drip coffee typically ranges from $2.50 to $3.50, while
                espresso-based drinks range from $4 to $5. Artisan spots like
                Revolver may charge a bit more for specialty pour-overs.
              </p>
            </div>

            {/* SECTION: Local Regulations / Best Practices */}
            <h2 id="local-regulations">Local Regulations / Best Practices</h2>
            <p>
              Vancouver encourages waste reduction with various policies such as
              banning certain single-use plastics. While cup fees have changed
              over time, many coffee shops still promote sustainability by
              incentivizing reusable cups. Always double-check hours and
              guidelines on official websites, as regulations may shift due to
              city policies or special circumstances.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Rainy weather is part of Vancouver’s charm, and these cozy coffee
              shops make a downpour feel downright welcoming. From the warm
              lights and jazz tunes at Greenhorn Espresso Bar to the garden-like
              atmosphere of The Garden Strathcona, each spot offers its own vibe
              and specialty drinks. So next time the forecast calls for rain,
              grab an umbrella and treat yourself to a warm, comforting beverage
              in one of the city’s best hideaways.
            </p>
            <p>
              <em>
                Disclaimer: Always check each cafe’s hours, menu, and special
                policies as they may change over time.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}